<script setup lang="ts">
/**
 * TutorialTooltip Component
 *
 * Displays the tutorial step content in a positioned tooltip.
 * Handles smart positioning relative to the target element.
 */

import { ref, computed, watch, onMounted } from 'vue'
import type { TutorialStep, TooltipPosition, SpotlightRect } from '@/types/tutorial'

const props = defineProps<{
  /** Current tutorial step */
  step: TutorialStep | null
  /** Cutout rectangle for positioning */
  cutout: SpotlightRect | null
  /** Whether this is the first step */
  isFirstStep: boolean
  /** Whether this is the last step */
  isLastStep: boolean
}>()

const emit = defineEmits<{
  next: []
  previous: []
  skip: []
}>()

const tooltipRef = ref<HTMLElement | null>(null)
const tooltipWidth = 360
const tooltipPadding = 16

/**
 * Format description text with basic markdown
 * Supports **bold** and *italic*
 */
function formatDescription(text: string | undefined): string {
  if (!text) return ''
  return text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
}

/**
 * Calculate tooltip position styles
 */
const tooltipStyles = computed(() => {
  // Center mode (no target element)
  if (!props.step?.target || !props.cutout) {
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }

  const position = props.step.position
  const rect = props.cutout
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const gap = 16 // Gap between element and tooltip

  let styles: Record<string, string> = {}

  switch (position) {
    case 'top':
      styles = {
        bottom: `${viewportHeight - rect.y + gap}px`,
        left: `${rect.x + rect.width / 2}px`,
        transform: 'translateX(-50%)'
      }
      break

    case 'bottom':
      styles = {
        top: `${rect.y + rect.height + gap}px`,
        left: `${rect.x + rect.width / 2}px`,
        transform: 'translateX(-50%)'
      }
      break

    case 'left':
      styles = {
        top: `${rect.y + rect.height / 2}px`,
        right: `${viewportWidth - rect.x + gap}px`,
        transform: 'translateY(-50%)'
      }
      break

    case 'right':
      styles = {
        top: `${rect.y + rect.height / 2}px`,
        left: `${rect.x + rect.width + gap}px`,
        transform: 'translateY(-50%)'
      }
      break

    case 'top-left':
      styles = {
        bottom: `${viewportHeight - rect.y + gap}px`,
        right: `${viewportWidth - rect.x - rect.width}px`
      }
      break

    case 'top-right':
      styles = {
        bottom: `${viewportHeight - rect.y + gap}px`,
        left: `${rect.x}px`
      }
      break

    case 'bottom-left':
      styles = {
        top: `${rect.y + rect.height + gap}px`,
        right: `${viewportWidth - rect.x - rect.width}px`
      }
      break

    case 'bottom-right':
      styles = {
        top: `${rect.y + rect.height + gap}px`,
        left: `${rect.x}px`
      }
      break

    case 'center':
    default:
      styles = {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }
  }

  return styles
})

/**
 * Check if tooltip is in centered/modal mode
 */
const isCentered = computed(
  () => !props.step?.target || props.step.position === 'center'
)
</script>

<template>
  <Transition name="tooltip-slide">
    <div
      v-if="step"
      ref="tooltipRef"
      class="tutorial-tooltip"
      :class="{ centered: isCentered }"
      :style="tooltipStyles"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="`tutorial-title-${step.id}`"
    >
      <!-- Header -->
      <div class="tooltip-header">
        <span
          v-if="step.icon"
          class="material-symbols-outlined tooltip-icon"
        >
          {{ step.icon }}
        </span>
        <h3 :id="`tutorial-title-${step.id}`" class="tooltip-title">
          {{ step.title }}
        </h3>
      </div>

      <!-- Content -->
      <div class="tooltip-body">
        <p
          class="tooltip-description"
          v-html="formatDescription(step.description)"
        />

        <img
          v-if="step.image"
          :src="step.image"
          :alt="step.title"
          class="tooltip-image"
        />

        <p v-if="step.actionHint" class="tooltip-action-hint">
          <span class="material-symbols-outlined">touch_app</span>
          {{ step.actionHint }}
        </p>
      </div>

      <!-- Footer with navigation -->
      <div class="tooltip-footer" :class="{ 'action-required': step.requiresAction }">
        <!-- Skip button - ALWAYS visible so users can escape if stuck -->
        <button
          class="btn-skip"
          @click="emit('skip')"
          aria-label="Skip tutorial"
        >
          Skip
        </button>

        <!-- Back button (not first step, not action-required) -->
        <button
          v-if="!isFirstStep && !step.requiresAction"
          class="btn-prev"
          @click="emit('previous')"
          aria-label="Previous step"
        >
          <span class="material-symbols-outlined">arrow_back</span>
          Back
        </button>

        <!-- Action required message -->
        <div v-if="step.requiresAction" class="action-required-message">
          <span class="material-symbols-outlined">touch_app</span>
          <span>Click to continue</span>
        </div>

        <!-- Next button (not shown when action required) -->
        <button
          v-if="!step.requiresAction"
          class="btn-next"
          @click="emit('next')"
          :aria-label="isLastStep ? 'Finish tutorial' : 'Next step'"
        >
          {{ isLastStep ? 'Finish' : 'Next' }}
          <span class="material-symbols-outlined">
            {{ isLastStep ? 'check' : 'arrow_forward' }}
          </span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.tutorial-tooltip {
  position: fixed;
  z-index: 10001;
  max-width: 360px;
  width: calc(100vw - 32px);
  background: linear-gradient(
    135deg,
    rgba(13, 31, 45, 0.98) 0%,
    rgba(26, 47, 63, 0.98) 100%
  );
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 16px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.6),
    0 0 24px rgba(232, 197, 71, 0.15);
  backdrop-filter: blur(8px);
  pointer-events: auto;
}

.tutorial-tooltip.centered {
  max-width: 440px;
}

/* Header */
.tooltip-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.tooltip-icon {
  font-size: 28px;
  color: #e8c547;
}

.tooltip-title {
  margin: 0;
  font-family: 'Cinzel', 'Playfair Display', Georgia, serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #f5f0e1;
}

/* Body */
.tooltip-body {
  padding: 16px 20px;
}

.tooltip-description {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
}

.tooltip-description :deep(strong) {
  color: #e8c547;
  font-weight: 600;
}

.tooltip-description :deep(em) {
  color: rgba(232, 197, 71, 0.8);
  font-style: italic;
}

.tooltip-image {
  display: block;
  width: 100%;
  margin-top: 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-action-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 0 0;
  padding: 8px 12px;
  background: rgba(232, 197, 71, 0.1);
  border-radius: 8px;
  font-size: 0.875rem;
  color: #e8c547;
}

.tooltip-action-hint .material-symbols-outlined {
  font-size: 18px;
}

/* Footer */
.tooltip-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.tooltip-footer button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-prev {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.8);
}

.btn-prev:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.btn-skip {
  margin-left: auto;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
}

.btn-skip:hover {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
}

.btn-next {
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
}

.btn-next:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
}

.tooltip-footer .material-symbols-outlined {
  font-size: 18px;
}

/* Action required footer - Skip button left, action message grows to fill */
.tooltip-footer.action-required {
  justify-content: flex-start;
}

.tooltip-footer.action-required .action-required-message {
  flex: 1;
  justify-content: center;
}

.action-required-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(232, 197, 71, 0.15);
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 8px;
  color: #e8c547;
  font-size: 0.875rem;
  font-weight: 500;
  animation: pulse-glow 2s ease-in-out infinite;
}

.action-required-message .material-symbols-outlined {
  font-size: 20px;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 8px rgba(232, 197, 71, 0.2);
  }
  50% {
    box-shadow: 0 0 16px rgba(232, 197, 71, 0.4);
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(3px);
  }
}

/* Transitions */
.tooltip-slide-enter-active,
.tooltip-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-slide-enter-from {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}

.tooltip-slide-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.95);
}

/* Responsive */
@media (max-width: 480px) {
  .tutorial-tooltip {
    max-width: calc(100vw - 24px);
    border-radius: 12px;
  }

  .tooltip-header {
    padding: 12px 16px 10px;
  }

  .tooltip-body {
    padding: 12px 16px;
  }

  .tooltip-footer {
    padding: 10px 12px 12px;
    flex-wrap: wrap;
  }

  .btn-skip {
    order: 3;
    width: 100%;
    margin: 8px 0 0;
    justify-content: center;
  }

  .btn-prev,
  .btn-next {
    flex: 1;
  }
}
</style>
